import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signUp, 
  signIn, 
  signOut, 
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttributes,
  updateUserAttribute,
  updatePassword,
  confirmUserAttribute
} from 'aws-amplify/auth';

// Note: Amplify is already configured in src/index.js
// We don't need to configure it again here

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const attributes = await fetchUserAttributes();
      
      // Get first and last name from attributes
      const firstName = attributes.given_name || attributes['custom:firstName'] || '';
      const lastName = attributes.family_name || attributes['custom:lastName'] || '';
      const fullName = firstName && lastName ? `${firstName} ${lastName}` : 
                      firstName || lastName || 
                      attributes.name || 
                      currentUser.username ||
                      currentUser.signInDetails?.loginId?.split('@')[0] || 
                      'User';
      
      setUser({
        id: currentUser.userId,
        userId: currentUser.userId,  // For GraphQL API
        sub: currentUser.userId,      // For Cognito
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId || currentUser.username,
        firstName: firstName,
        lastName: lastName,
        name: fullName,
        fullName: fullName,
        leagues: [],
        session: session
      });
      
      console.log('✅ User authenticated:', {
        userId: currentUser.userId,
        email: currentUser.signInDetails?.loginId || currentUser.username,
        fullName: fullName
      });
    } catch (error) {
      console.log('Not authenticated:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password: password
      });

      if (isSignedIn) {
        await checkUser();
        return { success: true };
      } else {
        // Handle MFA or other next steps
        return { success: false, nextStep };
      }
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Login failed');
    }
  };

  const register = async (firstName, lastName, email, password) => {
    try {
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email,
            given_name: firstName,
            family_name: lastName,
            name: `${firstName} ${lastName}`
          }
        }
      });

      if (isSignUpComplete) {
        // Auto-login after registration
        await login(email, password);
        return { success: true, userId };
      } else {
        // User needs to verify email/phone
        return { 
          success: false, 
          needsVerification: true,
          nextStep,
          message: 'Please check your email for verification code'
        };
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error(error.message || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error('Logout failed');
    }
  };

  const updateProfile = async (updates) => {
    try {
      const attrs = {};
      if (updates.given_name != null) attrs.given_name = updates.given_name;
      if (updates.family_name != null) attrs.family_name = updates.family_name;
      if (updates.email != null) attrs.email = updates.email;
      if (Object.keys(attrs).length === 0) return { success: true };
      const out = await updateUserAttributes({ userAttributes: attrs });
      await checkUser();
      return { success: true, nextStep: out?.nextStep };
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  };

  const updateUserEmail = async (email) => {
    try {
      const out = await updateUserAttribute({
        userAttribute: { attributeKey: 'email', value: email }
      });
      await checkUser();
      return { success: true, nextStep: out?.nextStep };
    } catch (error) {
      console.error('Update email error:', error);
      throw error;
    }
  };

  const confirmEmail = async (code) => {
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code });
      await checkUser();
      return { success: true };
    } catch (error) {
      console.error('Confirm email error:', error);
      throw error;
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await updatePassword({ oldPassword, newPassword });
      return { success: true };
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    checkUser,
    updateProfile,
    updateUserEmail,
    confirmEmail,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

